import { forwardRef, type HTMLAttributes } from 'react';
import clsx from 'clsx';

import { type SpaceStep, token } from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Divider.module.css';

export interface DividerProps extends HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
  /**
   * Purely visual separation. Drops the separator role so assistive technology
   * does not announce a structure that is not there.
   */
  decorative?: boolean;
  /** Symmetric margin along the divider's axis. */
  spacing?: SpaceStep;
  /** 2px top rule — the band boundary used above a metrics strip. */
  strong?: boolean;
}

export const Divider = forwardRef<HTMLDivElement, DividerProps>(function Divider(
  { orientation = 'horizontal', decorative = false, spacing, strong = false, className, style, ...rest },
  ref,
) {
  const vars: StyleWithVars = {
    // No spacing step is zero, so the default is the literal 0 — not a token.
    '--dpem-divider-spacing': spacing === undefined ? '0' : token.space[spacing],
  };

  return (
    <div
      {...rest}
      ref={ref}
      role={decorative ? 'presentation' : 'separator'}
      aria-orientation={decorative || orientation === 'horizontal' ? undefined : 'vertical'}
      className={clsx(
        styles.divider,
        orientation === 'vertical' ? styles.vertical : styles.horizontal,
        strong && styles.strong,
        className,
      )}
      style={mergeStyle(vars, style)}
    />
  );
});
